<template>
    <div class="flex flex-col items-center">
        <div class="w-full flex flex-col items-center space-y-4 mt-6 max-w-2xl">
            <!-- 新規登録セクション -->
            <div class="pt-12 pb-8 px-12 bg-white rounded-lg shadow-md w-full flex flex-col gap-8">
                <!-- エラーメッセージ -->
                <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />
                <div class="flex flex-col gap-4 text-center">
                    <h2 class="text-xl font-bold">新規登録をして利用を開始しましょう</h2>
                    <p class="text-sm">YouTubeでログインしているアカウントで登録してください。</p>
                </div>
                <button @click="handleGoogleAuth('register')" class="flex justify-center btn-secondary btn-lg w-full text-center">
                    <img :src="googleIcon" alt="Google icon" class="mr-2 w-4">Googleで登録する
                </button>
            </div>
    
            <!-- ログインセクション -->
            <div class="pt-12 pb-8 px-12 bg-white rounded-lg shadow-md w-full font-bold space-y-8">
                <h2 class="text-center text-lg">すでにアカウントをお持ちの方</h2>
                <button @click="handleGoogleAuth('login')" class="flex justify-center btn-secondary btn-lg w-full text-center">
                    ログインする
                </button>
            </div>
    
            <div class="w-full px-4 py-4 text-xs">
                livedoor ECHOESは、Google APIから受信した情報を他のアプリに使用および転送する場合、使用制限要件を含む<a href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes" target="_blank" class="link-styles">Google APIサービスユーザーデータポリシー</a>を遵守します。
            </div>
        </div>
    </div>
    </template>
    
    <script>
    import {
        ref,
        onMounted,
        inject,
    } from 'vue';
    import {
        useStore
    } from 'vuex';
    import {
        googleSdkLoaded
    } from 'vue3-google-login';
    import axios from 'axios';
    import {
        useRouter
    } from 'vue-router';
    import ErrorMessage from './ErrorMessage.vue';
    import googleIcon from '@/assets/google-color-icon.svg';
    
    export default {
        components: {
            ErrorMessage,
        },
        setup() {
            const store = useStore();
            const router = useRouter();
            const route = useRouter().currentRoute.value;
            const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
            const channelsDataList = ref([]); // APIから取得したチャンネルリスト
            const errorMessage = ref(''); // エラーメッセージの状態
            const fetchChannels = inject("fetchChannels");
    
            // エラーメッセージをクリアする関数
            const clearErrorMessage = () => {
                errorMessage.value = '';
            };

            // ブランドアカウント判定
            const isBrandAccount = (email) => {
                return email && email.includes('@pages.plusgoogle.com');
            };

            // URLクエリパラメータからの期限切れ情報を確認
        onMounted(() => {
            if (route.query.expired === 'token') {
                errorMessage.value = 'セキュリティのため、定期的な再認証が必要です。再度ログインしてください。';
            } else if (route.query.expired === 'jwt') { //JWTの期限切れはインターセプタからrouteすればキャッチ可能。今後のために一応分岐を入れるが、ブランドアカウントはそもそも関係ない
                errorMessage.value = 'セッションの有効期限が切れました。再度ログインしてください。';
            }
        });
    
            const sendAuthRequest = async (data) => {
                try {
                    const response = await axios.post(`${apiEndpoint}/user-auth/`, data, {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });
                    return response;
                } catch (error) {
                    errorMessage.value = error.response?.data?.error || 'Authentication failed'; // エラーメッセージをセット
                    console.error("Error in auth request", error);
                }
            };

            const handleGoogleAuth = async (action) => {
                let response;
                try {
                    if (action === 'login') {
                        response = await login();
                    } else if (action === 'register') {
                        response = await register();
                    }
                    if (response) {
                        const googleResponse = await sendAuthRequest({
                            access_token: response.access_token || null,
                            code: response.code || null,
                            action: action,
                            is_token_flow: response.is_token_flow || false // トークンフローで認証されたかを示すフラグ
                        });
    
                        if (googleResponse && googleResponse.status === 200 && action === 'register') {
                            // トークンログイン時刻を保存
                            localStorage.setItem('token_login_time', Date.now().toString());
                            if (googleResponse.data.type === 'multi') {
                                channelsDataList.value = googleResponse.data.channels_data_list; // チャンネルリストをセット
                            } else {
                                proceedToAccountCreation(googleResponse.data.channels_data_list, googleResponse.data.email, googleResponse.data.token);
                            }
                        } else if (googleResponse && googleResponse.status === 200 && action === 'login') {
                            // トークンログイン時刻を保存
                            localStorage.setItem('token_login_time', Date.now().toString());
                            await store.dispatch('login', {
                                user_id: googleResponse.data.user_id,
                                accessToken: googleResponse.data.accessToken,
                                expiry: googleResponse.data.expiry,
                                guideline: JSON.stringify(googleResponse.data.userConsent)
                            });

                            const channels = await fetchChannels(googleResponse.data.accessToken);
                            if (channels && channels.length > 0) {
                                router.push({
                                    name: 'VideoManagement'
                                });
                            } else {
                                router.push({
                                    name: 'ChannelInvitation'
                                });
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error in Google auth", error);
                    errorMessage.value = 'Google認証中にエラーが発生しました。再度お試しください。';
                }
            };
    
            
            // 共通の認証処理関数
// authenticateメソッドを更新
const authenticate = async (action) => {
    try {
        // ローカルストレージから過去の認証タイプを確認
        const email = localStorage.getItem('current_user_email');
        const authTypes = JSON.parse(localStorage.getItem('auth_types') || '{}');
        
        // 過去にこのメールアドレスでログインしていて、codeフロー可能かuncertain状態だと判断されている場合
        if (email && (authTypes[email] === 'code' || authTypes[email] === 'uncertain')) {
            const flowType = authTypes[email] === 'code' ? 'コードフロー可能' : '認証状態不確定';
            console.log(`${email} は${flowType}と記録されています。コードフローを試みます...`);
            
            try {
                return await performCodeFlow(action);
            } catch (e) {
                console.error('コードフロー失敗:', e);
                // performCodeFlow内でトークンフローへの切り替えが行われるため、
                // ここでの追加処理は不要
                throw e;
            }
        }
        
        // それ以外はtokenフローを使用
        console.log('トークンフローを使用します...');
        return await performTokenFlow();
    } catch (e) {
        console.error('認証エラー:', e);
        throw e;
    }
};
/*const authenticate = async (action) => {
    try {
        // ローカルストレージから過去の認証タイプを確認
        const email = localStorage.getItem('current_user_email');
        const authTypes = JSON.parse(localStorage.getItem('auth_types') || '{}');
        
        // 過去にこのメールアドレスでログインしていて、codeフロー可能だと判断されている場合
        if (email && authTypes[email] === 'code') {
            console.log(`${email} はコードフロー可能と記録されています。コードフローを試みます...`);
            try {
                return await performCodeFlow(action);
            } catch (e) {
                console.error('コードフロー失敗:', e);
                console.log('トークンフローに切り替えます...');
                // 失敗したら記録を更新
                authTypes[email] = 'token';
                localStorage.setItem('auth_types', JSON.stringify(authTypes));
                return await performTokenFlow();
            }
        }
        
        // それ以外はtokenフローを使用
        console.log('トークンフローを使用します...');
        const tokenResponse = await performTokenFlow();
        
        // サーバーにtokenと一緒にauth_type_checkリクエストを送信
        const authCheckResponse = await sendAuthRequest({
            access_token: tokenResponse.access_token,
            action: 'auth_type_check'
        });
        
        // メールアドレスとcode可否情報を保存
        const userEmail = authCheckResponse?.data?.email || '';
        const canUseCodeFlow = authCheckResponse?.data?.can_use_code_flow || false;
        
        if (userEmail) {
            localStorage.setItem('current_user_email', userEmail);
            authTypes[userEmail] = canUseCodeFlow ? 'code' : 'token';
            localStorage.setItem('auth_types', JSON.stringify(authTypes));
            //console.log(`${userEmail} の認証タイプを ${authTypes[userEmail]} として記録しました`);
        }
        
        return tokenResponse;
    } catch (e) {
        console.error('認証エラー:', e);
        throw e;
    }
};*/
    
            // コードフローを実行する関数
            const performCodeFlow = () => { //action
                return new Promise((resolve, reject) => {
                    let isResolved = false; // 処理が完了したかどうかを追跡するフラグ

                    try {
                        // タイムアウト処理を追加
                        const timeoutId = setTimeout(() => {
                            if (isResolved) return;
                            isResolved = true;

                            console.error('コードフロータイムアウト: 応答がありません');
                            // タイムアウト時はauth_typeを更新せず、トークンフロー後に判断

                            // エラーメッセージを表示して、ユーザーに固まったウィンドウを閉じるように促す
                            errorMessage.value = 'ログイン処理がタイムアウトしました。開いたままのGoogle認証ウィンドウがある場合は閉じてください。別の認証方法で再試行します。';
                            
                            
                            // トークンフローに切り替え
                            console.log('トークンフローに切り替えます...');
                            performTokenFlow().then(resolve).catch(reject);
                        }, 60000); // 60秒のタイムアウト

                        googleSdkLoaded((google) => {
                            const codeClient = google.accounts.oauth2.initCodeClient({
                                client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
                                scope: 'email profile openid https://www.googleapis.com/auth/youtube.readonly',
                                callback: (response) => { 
                                    if (isResolved) return;
                                    isResolved = true;
                                    clearTimeout(timeoutId); // タイムアウトをクリア
                                    
                                    // コードフローが成功したら、現在のメールアドレスをcodeフロー可能として記録
                                    const email = localStorage.getItem('current_user_email');
                                    if (email) {
                                        const authTypes = JSON.parse(localStorage.getItem('auth_types') || '{}');
                                        // ブランドアカウントでなければcodeフロー可能として記録
                                        if (!isBrandAccount(email)) {
                                            authTypes[email] = 'code';
                                            localStorage.setItem('auth_types', JSON.stringify(authTypes));
                                            console.log(`${email}の認証タイプをcodeとして記録しました`);
                                        }
                                    }
                                    
                                    resolve(response);

                                },
                                error_callback: (error) => {
                                    if (isResolved) return;
                                    isResolved = true;
                                    clearTimeout(timeoutId); // タイムアウトをクリア
                                    
                                    console.error('コードフローエラー:', error);
                                    
                                    // コードフローが失敗した場合、トークンフローに切り替える
                                    console.log('トークンフローに切り替えます...');
                                    performTokenFlow().then(resolve).catch(reject);
                                }
                            });
                            
                            codeClient.requestCode();
                        });
                    } catch (e) {
                        console.error('コードフロー初期化エラー:', e);
                        reject(e);
                    }
                });
            };
    
            // トークンフローを実行する関数
const performTokenFlow = () => {
    return new Promise((resolve, reject) => {
        try {
            // 新しいフローを開始する前にエラーメッセージをクリア
            // タイムアウトメッセージが表示されている場合は、
            // 「別の認証方法を試しています...」のような進行状況に更新
            if (errorMessage.value && errorMessage.value.includes('タイムアウト')) {
                errorMessage.value = 'ログイン処理がタイムアウトしました。開いたままのGoogle認証ウィンドウがある場合は閉じてください。別の認証方法で再試行します。';
            }
            googleSdkLoaded((google) => {
                const tokenClient = google.accounts.oauth2.initTokenClient({
                    client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
                    scope: 'email profile openid https://www.googleapis.com/auth/youtube.readonly',
                    callback: async (tokenResponse) => {

                        if (tokenResponse.error) {
                            console.log('トークンエラー:', tokenResponse);
                            reject(tokenResponse);
                            return;
                        }
                        //console.log('トークンフロー成功:', tokenResponse);
                        
                        // アクセストークンを使ってYouTubeチャンネル情報を取得
                        try {

                            const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                                headers: {
                                    'Authorization': `Bearer ${tokenResponse.access_token}`
                                }
                            });
                            const userInfo = await userInfoResponse.json();
                            if (userInfo.email) {
                                // メールアドレスを保存
                                localStorage.setItem('current_user_email', userInfo.email);
                                
                                // 認証タイプ情報を更新
                                const authTypes = JSON.parse(localStorage.getItem('auth_types') || '{}');
                                
                                // ブランドアカウントの場合は必ずトークンフローに固定
                                if (isBrandAccount(userInfo.email)) {
                                    authTypes[userInfo.email] = 'token';
                                    console.log(`${userInfo.email} はブランドアカウントです。認証タイプをtokenに設定しました`);
                                } 
                                // 一般アカウントで新規または状態不明の場合は、次回コードフローを試す
                                else if (!authTypes[userInfo.email] || authTypes[userInfo.email] === 'uncertain') {
                                    authTypes[userInfo.email] = 'uncertain';
                                    console.log(`${userInfo.email} の認証タイプをuncertainとして記録しました`);
                                }
                                // それ以外の場合は既存の設定を維持
                                
                                localStorage.setItem('auth_types', JSON.stringify(authTypes));
                            }
                            /*
                            // YouTube APIを呼び出して認証を検証
                            await fetch('https://www.googleapis.com/youtube/v3/channels?part=snippet&mine=true', {
                                headers: {
                                    'Authorization': `Bearer ${tokenResponse.access_token}`
                                }
                            });*/

                            // バックエンドとの互換性のためにレスポンス形式を変更
                            resolve({
                                code: tokenResponse.access_token, // バックエンドが期待するcode
                                access_token: tokenResponse.access_token,
                                is_token_flow: true // トークンフローで認証されたことを示すフラグ
                            });
                            tokenResponse = null;
                        } catch (e) {
                            console.error('YouTube API Error:', e);
                            reject(e);
                        }
                    },
                    error_callback: (error) => {
                        console.error('トークンクライアントエラー:', error);
                        reject(error);
                    }
                });
                
                console.log('トークンフローを開始します...');
                // ここでは prompt オプションを削除（必要に応じ 'select_account' などを指定検討）
                tokenClient.requestAccessToken({ prompt: 'select_account' });
                //tokenClient.requestAccessToken();
            });
        } catch (e) {

            console.error('トークンフロー初期化エラー:', e);
            reject(e);
        }
    });
};
    
            // ログイン処理
            const login = async () => {
                return authenticate('login');
            };
    
            // 登録処理
            const register = async () => {
                return authenticate('register');
            };
    
            const proceedToAccountCreation = (channelData, email, token) => {
                store.commit('setChannelData', channelData); // VuexのstoreにchannelDataをセット
                store.commit('setemail', email);
                store.commit('settoken', token);
                router.push({
                    name: 'AccountCreation',
                });
            };
    
            const onChannelSelected = (channelData) => {
                proceedToAccountCreation(channelData); // アカウント作成処理を続行
            };
    
            return {
                channelsDataList,
                handleGoogleAuth,
                onChannelSelected,
                errorMessage,
                clearErrorMessage,
                googleIcon
            }
        }
    };
    
    </script>
